/**
 * This file centralizes all design tokens and Tailwind class combinations 
 * to ensure consistency across the application and allow for single-point theme updates.
 */

export const theme = {
  colors: {
    primary: 'var(--brand-primary)',
    primary700: 'var(--brand-primary-hover)',
    secondary: 'var(--color-sec)',
    accent: 'var(--color-acc)',
    bg: 'var(--bg-app)',
    surface: 'var(--bg-surface)',
    text: 'var(--text-main)',
    muted: 'var(--text-muted)',
    subtle: 'var(--text-muted)',
    border: 'var(--border-main)',
    success: "text-emerald-600",
    danger: "text-rose-600",
    warning: "text-amber-600",
    info: "text-[var(--brand-primary)]",
    brandBg: "bg-[var(--brand-primary)]",
    brandText: "text-[var(--brand-primary)]",
    brandLight: "bg-[var(--brand-50)] text-[var(--brand-primary)]",
    brandSubtle: "bg-[var(--brand-50)]/40",
    bgApp: "bg-[var(--bg-app)]",
    bgSubtle: "bg-[var(--bg-surface)]",
    borderSubtle: "border-slate-100 dark:border-slate-800",
    textHeading: "text-slate-700 dark:text-slate-200",
    textBody: "text-slate-500 dark:text-slate-400",
    textMuted: "text-slate-400 dark:text-slate-500",
    textDark: "text-[var(--text-main)]",
  },

  // Layout & Containers
  layout: {
    app: "flex bg-[var(--bg-app)] min-h-screen font-sans transition-colors duration-300",
    main: "flex-1 flex flex-col min-h-screen relative",
    container: "p-4 md:px-[21px] md:pt-[21px] md:pb-[31px] flex-1 w-full animate-in fade-in slide-in-from-bottom-4 duration-500",
    header: "h-24 px-4 md:px-[21px] flex items-center justify-between sticky top-0 z-10 bg-[var(--bg-app)]/80 border-b border-slate-200/50 transition-colors duration-300",
    sidebar: "hidden lg:flex w-60 bg-[var(--bg-app)] border-r border-slate-200/50 flex flex-col h-screen sticky top-0 transition-colors duration-300",
    drawer: "fixed inset-0 z-50 lg:hidden",
    drawerContent: "fixed inset-y-0 left-0 w-72 bg-[var(--bg-app)] shadow-2xl z-50 transform transition-transform duration-300 ease-in-out",
    drawerOverlay: "fixed inset-0 bg-slate-900/60 z-40 animate-in fade-in duration-300 backdrop-blur-none",
    grid: "grid grid-cols-1 md:grid-cols-12 gap-4 auto-rows-min",
    gridCols2: "grid grid-cols-1 md:grid-cols-2 gap-4",
    gridCols3: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4",
    gridCols4: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4",
    stack: "space-y-4 md:space-y-4",
    stackSmall: "space-y-2 md:space-y-3",
    flexBetween: "flex items-center justify-between",
    flexCenter: "flex items-center justify-center",
    actionGroup: "flex gap-3 w-full md:w-auto",
    actionButton: "flex-1 md:flex-none",
    formField: "space-y-1.5",
    checkboxGroup: "flex gap-6 mt-2 col-span-full",
    // Plan 7.7: responsive modals — full width capped on mobile, centered on sm+
    modalOverlay: "fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-slate-900/70 animate-in fade-in duration-300 backdrop-blur-none",
    modalContent: "bg-white dark:bg-slate-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-lg sm:max-w-xl md:max-w-2xl lg:max-w-3xl xl:max-w-4xl max-h-[90vh] flex flex-col overflow-hidden",
    modalContentSmall: "bg-white dark:bg-slate-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg overflow-hidden",
    colSpanFull: "col-span-full",

    // High-level Section Layouts
    section: "py-24 px-6 max-w-7xl mx-auto",
    heroSection: "pt-40 pb-24 px-6 max-w-7xl mx-auto text-center",
    footer: "py-12 px-6 bg-white border-t border-slate-200",
    nav: "fixed w-full z-50 bg-white/90 border-b border-slate-200/50",
    navInner: "max-w-7xl mx-auto px-6 h-20 flex items-center justify-between",
  },

  // Grid Cell Spans (Bento Grid Patterns)
  grid: {
    span3: "md:col-span-3",
    span4: "md:col-span-4",
    span6: "md:col-span-6",
    span8: "md:col-span-8",
    span12: "md:col-span-12",
    row2: "md:row-span-2",
    row3: "md:row-span-3",
  },

  // Component Styles
  components: {
    card: "bg-white rounded-xl shadow-subtle border border-slate-200 overflow-hidden transition-all duration-300",
    cardInteractive: "hover:shadow-float hover:-translate-y-1 cursor-pointer",
    cardHeader: "p-4 flex items-center justify-between border-b border-slate-50",
    cardBody: "p-4",
    cardTitle: "text-[10px] font-bold text-slate-600 uppercase tracking-widest",
    cardActions: "absolute top-4 right-4 flex gap-2 transition-all z-10",
    cardActionButton: "h-8 w-8 bg-white/80 backdrop-blur-sm shadow-sm border border-slate-100",
    cardActionIcon: "text-blue-600",
    cardActionIconDanger: "text-rose-600",
    cardModule: "group relative flex flex-col h-full overflow-hidden p-0 border-slate-200/60 shadow-sm hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-500",


    button: {
      base: "inline-flex items-center justify-center font-bold transition-all focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]/20 active:scale-95 disabled:opacity-50 disabled:pointer-events-none rounded-xl",
      primary: "bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-hover)] shadow-xs hover:shadow-sm",
      secondary: "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700",
      outline: "border border-slate-200 dark:border-slate-700 bg-transparent hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium",
      ghost: "bg-transparent hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 font-medium",
      danger: "bg-rose-600 text-white hover:bg-rose-700 shadow-xs",
      link: "text-xs font-bold flex items-center gap-1 text-[var(--brand-primary)] hover:underline transition-colors group/btn",
      frostedAction: "h-[27px] w-[27px] rounded-xl bg-white/80 dark:bg-slate-800/80 backdrop-blur-xs border border-slate-200/80 dark:border-slate-700/80 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-700 shadow-2xs p-0 flex items-center justify-center",
      dashed: "w-full mt-6 text-[11px] font-bold text-slate-400 border border-dashed border-slate-200 py-3 rounded-xl hover:bg-slate-50 transition-colors",
    },

    label: "text-[10px] font-bold uppercase text-slate-400 mb-1.5 block tracking-widest",
    input: "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand-500/20 focus:border-brand-300 transition-all outline-none text-slate-700 placeholder:text-slate-400 hover:border-slate-300",
    select: "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand-500/20 focus:border-brand-300 transition-all outline-none text-slate-700 hover:border-slate-300",
    form: {
      label: "text-[10px] font-bold uppercase text-slate-400 mb-1.5 block tracking-widest",
      input: "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand-500/20 focus:border-brand-300 transition-all outline-none text-slate-700 placeholder:text-slate-400 hover:border-slate-300",
      select: "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand-500/20 focus:border-brand-300 transition-all outline-none text-slate-700 hover:border-slate-300",
    },
    fieldset: "col-span-full mt-4 p-5 border border-slate-200 rounded-xl bg-slate-50/50 shadow-sm",
    fieldsetTitle: "font-bold text-sm text-slate-700",

    checkbox: {
      label: "flex items-center gap-2 text-sm font-bold cursor-pointer text-slate-700",
      input: "w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500",
    },

    modal: {
      form: "flex flex-col h-full overflow-hidden",
      header: "flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50/50",
      closeButton: "p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors",
      body: "flex-1 overflow-y-auto p-6 custom-scrollbar",
      footer: "p-4 border-t border-slate-100 bg-slate-50/80",
    },

    alert: {
      error: "text-center p-4 text-rose-500 font-bold bg-rose-50 rounded-xl border border-rose-100 flex items-center gap-3",
      warning: "p-4 bg-amber-50 rounded-xl border border-amber-100 flex gap-3 text-sm text-amber-800 font-medium leading-relaxed",
      success: "p-4 bg-emerald-50 rounded-xl border border-emerald-100 flex gap-3 text-sm text-emerald-800 font-medium leading-relaxed",
      danger: "bg-red-100 text-red-700 rounded-2xl p-4 flex items-start gap-3",
      successLarge: "bg-emerald-100 text-emerald-800 rounded-2xl p-4 flex items-center gap-3",
    },

    toast: {
      success: "bg-emerald-50 text-emerald-800 border-emerald-100",
      error: "bg-rose-50 text-rose-800 border-rose-100",
      info: "bg-brand-50 text-brand-800 border-brand-100",
      successIcon: "text-emerald-500",
      errorIcon: "text-rose-500",
      infoIcon: "text-brand-500",
    },

    iconBadge: {
      base: "w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all shadow-sm",
      primary: "bg-[var(--brand-50)] text-[var(--brand-primary)] border border-[var(--brand-200)]",
      brand: "bg-[var(--brand-50)] text-[var(--brand-primary)] border border-[var(--brand-200)]",
      secondary: "bg-cyan-50 dark:bg-cyan-950/60 text-cyan-600 dark:text-cyan-400 border border-cyan-100 dark:border-cyan-900",
      success: "bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900",
      danger: "bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 border border-rose-100 dark:border-rose-900",
      lg: "w-12 h-12 rounded-xl shadow-sm",
    },


    listItem: {
      base: "flex gap-3 items-start group",
      content: "flex-1 min-w-0",
      header: "flex items-center justify-between gap-2 mb-1",
    },

    trend: {
      up: "flex items-center gap-1 mt-2 text-xs font-semibold text-emerald-600",
      down: "flex items-center gap-1 mt-2 text-xs font-semibold text-rose-600",
      neutral: "flex items-center gap-1 mt-2 text-xs font-semibold text-slate-400",
    },

    chart: {
      container: "h-[350px] w-full pt-4",
      legendRow: "flex items-center gap-4",
      legendItem: "flex items-center gap-2",
      dot: "w-3 h-3 rounded-full",
    },

    table: {
      container: "p-0 overflow-hidden",
      head: "bg-slate-50/50 dark:bg-slate-800/50 text-[10px] uppercase tracking-widest text-slate-400 font-bold",
      th: "px-6 py-4 text-left",
      thRight: "px-6 py-4 text-right",
      thCenter: "px-6 py-4 text-center",
      td: "px-6 py-4",
      tdRight: "px-6 py-4 text-right",
      tdCenter: "px-6 py-4 text-center",
      row: "hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors group border-b border-slate-50 dark:border-slate-800 last:border-0",
      divide: "divide-y divide-slate-50 dark:divide-slate-800",
    },

    badge: {
      base: "px-2 py-1 rounded-md text-[10px] font-bold uppercase",
      brand: "bg-[var(--brand-50)] text-[var(--brand-primary)] border border-[var(--brand-200)]",
      secondary: "bg-secondary/10 text-secondary",
      success: "bg-emerald-50 text-emerald-600",
      warning: "bg-amber-50 text-amber-600",
      danger: "bg-rose-50 text-rose-600",
      accent: "bg-accent/10 text-accent",
    },

    tabs: {
      container: "flex border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50",
      tab: "px-6 py-4 text-sm font-bold capitalize transition-all border-b-2",
      active: "border-[var(--brand-primary)] text-[var(--brand-primary)] bg-white dark:bg-slate-800",
      inactive: "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-100",
    },

    segmentedControl: {
      container: "flex p-1 bg-slate-100 rounded-2xl w-full max-w-sm mx-auto",
      button: "flex-1 py-2.5 rounded-xl transition-all",
      activeSuccess: "bg-white text-emerald-600 shadow-sm",
      activeDanger: "bg-white text-rose-600 shadow-sm",
      inactive: "text-slate-400 hover:text-slate-600",
    },

    skeleton: "bg-slate-100 rounded-2xl animate-pulse",

    navItem: {
      base: "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all group relative font-medium text-sm",
      active: "bg-brand-50 text-brand-600",
      inactive: "text-slate-500 hover:bg-slate-100 hover:text-slate-700",
    },

    avatar: "w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center font-bold text-slate-500 text-[10px]",
    divider: "h-px bg-slate-100 my-4",
    progressBar: {
      container: "h-1.5 rounded-full bg-slate-100 overflow-hidden",
      bar: "h-full transition-all duration-500",
    },

    // Form Helpers
    requiredAsterisk: "text-rose-500 ml-0.5",
    inputError: "border-rose-400 focus:ring-rose-400/20",
    errorText: "text-xs text-rose-500 mt-0.5",
    inlineAlert: "text-xs font-semibold px-3 py-2 rounded-lg",

    // Decorative & Translucent
    logo: {
      base: "flex items-center justify-center font-bold bg-brand-600 text-white",
      lg: "w-10 h-10 rounded-2xl text-xl",
      md: "w-8 h-8 rounded-xl text-base",
    },
    decorative: {
      glass: "bg-white/10 border border-white/10",
      glassDark: "bg-slate-900/10 border border-slate-900/10",
      grid: "bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiI+PHBhdGggZD0iTTAgMzJoMzJWME0wIDBoMzJ2MzJNMzIgMzJIMFYwIiBmaWxsPSJub25lIiBzdHJva2U9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiLz48L3N2Zz4=')]",
      glow: "absolute -inset-3 opacity-30 bg-brand-500/20 rounded-[2.5rem]",
      panel: "relative rounded-[2rem] p-4 bg-white border border-slate-200 shadow-2xl",
    },

    // Additional UI Patterns
    cardSection: "px-[21px] pt-6 pb-5 space-y-4",
    cardFooter: "px-[21px] py-3 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between",
    cardProfileHeader: "flex items-center gap-4 mb-[14px] pr-8",
    cardProfileInfo: "min-w-0 flex-1",
    cardProfileContactRow: "flex flex-wrap items-center gap-x-3 gap-y-1",
    cardProfileContactItem: "flex items-center gap-1 text-slate-400 truncate max-w-[150px]",
    cardProfileStatsGrid: "grid grid-cols-[1fr_auto] gap-x-4 gap-y-3 pt-3 border-t border-slate-100",
    cardProfileStatLeft: "min-w-0",
    cardProfileStatRight: "text-right whitespace-nowrap",
    pagination: "px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between",
    paginationButton: "w-8 h-8 rounded-lg text-xs font-bold transition-all flex items-center justify-center",
    filterPill: "flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-xl border border-slate-100 shadow-sm",
    expandedRow: "overflow-hidden bg-slate-50/50 border-y border-slate-100",
    badgeLarge: "px-3 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wider",
    actionPill: "flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white shadow-sm hover:bg-slate-50 transition-all",

    // Sidebar Patterns
    sidebarContainer: "fixed inset-y-0 left-0 z-50 lg:relative lg:z-0 bg-white dark:bg-slate-900 border-r border-slate-100 dark:border-slate-800 flex flex-col transition-all duration-300 ease-in-out",
    sidebarItem: "flex items-center gap-3 w-full p-3 rounded-xl transition-all duration-200 group relative text-slate-500 hover:bg-slate-50 hover:text-[var(--brand-primary)]",
    sidebarItemActive: "flex items-center gap-3 w-full p-3 rounded-xl transition-all duration-200 group relative bg-[var(--brand-primary)] text-white shadow-lg shadow-[var(--theme-glow)]",
    sidebarTooltip: "absolute left-full ml-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50",
    logoutButton: "flex items-center gap-3 w-full p-3 rounded-xl transition-all text-rose-500 hover:bg-rose-50 group",

    // Form & Input Patterns
    inputIconLeft: "absolute left-4 top-1/2 -translate-y-1/2 transition-colors",
    inputIconRight: "absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none",

    // State Patterns
    emptyState: "py-20 flex flex-col items-center justify-center text-center border-dashed border-2 border-slate-100 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-900/50 rounded-[2rem]",
    emptyStateIcon: "w-20 h-20 bg-slate-100/50 dark:bg-slate-800/50 text-slate-400 rounded-3xl flex items-center justify-center mb-6",

    // Selection Patterns
    selectableCard: "flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 hover:border-slate-200 dark:hover:border-slate-700",
    selectableCardActive: "border-[var(--brand-primary)] bg-[var(--brand-50)]/50 text-[var(--brand-primary)] shadow-sm",
    selectableRow: "flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 hover:border-slate-200 dark:hover:border-slate-700",
    selectableRowActive: "border-[var(--brand-primary)] bg-[var(--brand-50)]/50 text-[var(--brand-primary)] shadow-sm",

    // Navigation Patterns
    tabContainer: "flex items-center gap-1.5 p-1.5 bg-white dark:bg-slate-900 rounded-2xl w-fit border border-slate-200/80 dark:border-slate-800 shadow-2xs",
    tabItem: "px-3.5 sm:px-4 py-2 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer shrink-0 select-none text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800",
    tabItemActive: "bg-brand-600 text-white shadow-xs scale-[1.01] hover:text-white hover:bg-brand-600",

    // Upload & Area Patterns
    uploadArea: "h-full flex items-center justify-center border-2 border-dashed border-slate-100 rounded-[2.5rem] p-12 transition-colors hover:border-brand-200 hover:bg-slate-50/50",
    uploadAreaIcon: "w-16 h-16 bg-slate-50 text-slate-300 rounded-2xl flex items-center justify-center mx-auto",
    successArea: "bg-emerald-50/50 border border-emerald-100 rounded-[2.5rem] p-12 text-center space-y-6",
    successAreaIcon: "w-20 h-20 bg-emerald-100 text-emerald-600 rounded-3xl flex items-center justify-center mx-auto shadow-lg shadow-emerald-500/20",

    // Feedback & Tip Patterns
    infoTip: "mt-6 p-4 bg-blue-50/50 rounded-2xl border border-blue-100 flex gap-3",
    infoTipIcon: "text-blue-500 shrink-0 mt-0.5",
    infoTipText: "text-[11px] text-blue-700 leading-relaxed",

    // Sub-section Patterns
    subSectionHeader: "md:col-span-2 border-b border-slate-100 pb-2 mb-4",
    neutralSubSection: "p-4 rounded-xl bg-slate-50 border border-slate-100",
    secondaryInput: "px-3 py-2 border border-slate-200 rounded-lg text-[13px] bg-white outline-none focus:ring-1 focus:ring-brand-500 transition-all",
    readOnlyInput: "bg-slate-50 text-slate-500 cursor-not-allowed",
  },

  // Typography Tokens
  typography: {
    h1: "text-xl font-bold text-slate-700 tracking-tight",
    h2: "text-lg font-semibold text-slate-700 tracking-tight",
    h3: "text-base font-semibold text-slate-700",
    h4: "text-sm font-semibold text-slate-700",
    body: "text-[13px] text-slate-500 leading-relaxed",
    bodySmall: "text-[12px] text-slate-900",
    bodyExtraSmall: "text-[10px] text-slate-700 font-medium",
    caption: "text-[10px] font-semibold text-slate-400 uppercase tracking-wider",
    formLabel: "text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1.5",
    label: "text-xs font-semibold text-slate-700",
    statLabel: "text-[13px] text-slate-500 leading-relaxed", // Matches Dashboard label style
    statValue: "text-xl font-bold text-slate-700 tracking-tight", // Professional large numbers
    heroTitle: "text-3xl lg:text-4xl font-bold leading-tight tracking-tight",
    heroSubtitle: "text-base font-medium text-slate-500",
    brandLabel: "text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em]",
    quote: "text-sm italic leading-relaxed",
    brandBody: "text-[13px] text-white/80 leading-relaxed",
    brandBodySmall: "text-xs text-white/60",
    brandH2: "text-2xl lg:text-3xl font-bold leading-tight",
    lead: "text-base md:text-lg text-slate-500 leading-relaxed",
    heroText: "text-xl md:text-2xl text-slate-500 leading-relaxed",
    heroDisplay: "text-5xl md:text-7xl font-bold tracking-tight leading-[1.1]",
    sectionTitle: "text-3xl md:text-4xl font-bold leading-tight",
    sectionSubtitle: "text-base text-slate-500",
    brandDisplay: "text-5xl font-bold",
    brandLead: "text-base text-white/70 leading-relaxed",
  }
};
